import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellRing, BriefcaseBusiness, Building2, CalendarDays, CreditCard, FileText, MessageSquare, Save, UserRoundCheck } from 'lucide-react';
import { fetchNotificationPreferences, updateNotificationPreferences } from '@/lib/api';
import type { NotificationPreferences, NotificationPriority } from '@/types';
import { useToast } from '@/contexts/ToastContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';

const DEFAULT_PREFERENCES: NotificationPreferences = {
  inAppMentions: true,
  inAppPipeline: true,
  inAppInterviews: true,
  inAppSubmissions: true,
  inAppInvoices: true,
  inAppPlacements: true,
  inAppJobs: false,
  inAppCandidates: false,
  inAppCompanies: false,
  inAppSystem: true,
  minPriority: 'normal',
  emailDigest: false,
};

const PREFERENCE_ROWS: Array<{
  key: keyof Omit<NotificationPreferences, 'minPriority'>;
  label: string;
  description: string;
  icon: typeof Bell;
}> = [
  {
    key: 'inAppMentions',
    label: 'Mentions in notes',
    description: 'Notify me when a teammate mentions me on candidates, jobs, applications, or companies.',
    icon: MessageSquare,
  },
  {
    key: 'inAppPipeline',
    label: 'Pipeline changes',
    description: 'Important candidate stage changes such as interview, offer, hired, or disqualified.',
    icon: UserRoundCheck,
  },
  {
    key: 'inAppInterviews',
    label: 'Interviews',
    description: 'Interview scheduled, rescheduled, cancelled, completed, or feedback-needed events.',
    icon: CalendarDays,
  },
  {
    key: 'inAppSubmissions',
    label: 'Client submissions',
    description: 'Candidate submissions and client feedback requiring follow-up.',
    icon: FileText,
  },
  {
    key: 'inAppInvoices',
    label: 'Invoices and payments',
    description: 'Overdue invoices, payments received, and finance follow-up events.',
    icon: CreditCard,
  },
  {
    key: 'inAppPlacements',
    label: 'Placements',
    description: 'Placement created, confirmed, changed, or start-date attention events.',
    icon: BriefcaseBusiness,
  },
  {
    key: 'inAppJobs',
    label: 'Job updates',
    description: 'Lower-noise job status and assignment changes. Generic edits remain suppressed.',
    icon: BriefcaseBusiness,
  },
  {
    key: 'inAppCandidates',
    label: 'Candidate updates',
    description: 'Candidate ownership/status changes. Generic profile cleanup remains suppressed.',
    icon: UserRoundCheck,
  },
  {
    key: 'inAppCompanies',
    label: 'Company updates',
    description: 'Company ownership/client-status changes and client follow-up events.',
    icon: Building2,
  },
  {
    key: 'inAppSystem',
    label: 'System notifications',
    description: 'Administrative or system-level notices relevant to your account.',
    icon: BellRing,
  },
  {
    key: 'emailDigest',
    label: 'Email digest placeholder',
    description: 'Keep digest preference stored for future email delivery. This does not send email yet.',
    icon: Bell,
  },
];

function mergePreferences(preferences?: NotificationPreferences): NotificationPreferences {
  return { ...DEFAULT_PREFERENCES, ...(preferences ?? {}) };
}

export function NotificationPreferencesSettings() {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: fetchNotificationPreferences,
  });

  const preferences = mergePreferences(data);

  const updateMutation = useMutation({
    mutationFn: updateNotificationPreferences,
    onSuccess: (updated) => {
      queryClient.setQueryData(['notification-preferences'], updated);
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      addToast({ type: 'success', title: 'Notification preferences saved' });
    },
    onError: (error: Error) => {
      addToast({ type: 'error', title: 'Failed to save notification preferences', message: error.message });
    },
  });

  const updatePreference = <K extends keyof NotificationPreferences>(key: K, value: NotificationPreferences[K]) => {
    updateMutation.mutate({ [key]: value } as Partial<NotificationPreferences>);
  };

  if (isLoading) {
    return (
      <Card className="max-w-3xl">
        <CardHeader>
          <Skeleton className="h-6 w-48" />
          <Skeleton className="h-4 w-80" />
        </CardHeader>
        <CardContent className="space-y-4">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-14 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return (
      <Card className="max-w-3xl">
        <CardHeader>
          <CardTitle>Notifications</CardTitle>
          <CardDescription>Could not load your notification preferences.</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Bell className="h-5 w-5 text-emerald-400" />
          Notifications
        </CardTitle>
        <CardDescription>
          Choose which high-signal CRM events should create in-app notifications. Low-value create/edit/view noise stays suppressed.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label>Minimum priority</Label>
          <Select
            value={preferences.minPriority}
            onValueChange={(value) => updatePreference('minPriority', value as NotificationPriority)}
            disabled={updateMutation.isPending}
          >
            <SelectTrigger className="max-w-xs">
              <SelectValue placeholder="Select minimum priority" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="low">Low and above</SelectItem>
              <SelectItem value="normal">Normal and above</SelectItem>
              <SelectItem value="high">High and urgent only</SelectItem>
              <SelectItem value="urgent">Urgent only</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-sm text-text-tertiary">
            Mentions and urgent business events should normally stay enabled at normal priority or above.
          </p>
        </div>

        <div className="divide-y divide-white/10 rounded-lg border border-white/10">
          {PREFERENCE_ROWS.map((row) => {
            const Icon = row.icon;
            const checked = Boolean(preferences[row.key]);
            return (
              <label
                key={row.key}
                className="flex items-start gap-4 p-4 hover:bg-white/[0.02] transition-colors cursor-pointer"
              >
                <Checkbox
                  checked={checked}
                  disabled={updateMutation.isPending}
                  onCheckedChange={(next) => updatePreference(row.key, next)}
                  className="mt-1"
                />
                <Icon className="h-5 w-5 mt-0.5 text-emerald-400 flex-shrink-0" />
                <div className="flex-1">
                  <p className="font-medium text-text-primary">{row.label}</p>
                  <p className="text-sm text-text-tertiary mt-0.5">{row.description}</p>
                </div>
              </label>
            );
          })}
        </div>

        <div className="flex items-center gap-2 text-sm text-text-tertiary">
          <Save className="h-4 w-4" />
          Changes save immediately and only affect your own notification feed.
        </div>

        {updateMutation.isPending && (
          <p className="text-sm text-emerald-400">Saving preference...</p>
        )}
      </CardContent>
    </Card>
  );
}
